import {
  useState,
  useCallback,
  useRef,
  useEffect,
  type ChangeEvent,
  type SyntheticEvent,
} from "react";
import {
  Box,
  Tabs,
  Tab,
  Typography,
  Button,
  Alert,
  Card,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Stack,
  CircularProgress,
} from "@mui/material";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { useFormContext, useWatch } from "react-hook-form";
import { useRecipientSelection } from "@/containers/whatsapp-broadcasts/context";
import AdvancedFilterBuilder from "./advanced-filter-builder";
import MiqaatRsvpAudienceFilters from "./MiqaatRsvpAudienceFilters";
import EventRsvpRecipientPanel from "./EventRsvpRecipientPanel";
import FmbOutstandingRecipientPanel from "./FmbOutstandingRecipientPanel";
import SabilOutstandingRecipientPanel from "./SabilOutstandingRecipientPanel";
import MuttavatteenOutstandingRecipientPanel from "./MuttavatteenOutstandingRecipientPanel";
import { useLookupIts, usePreviewMiqaatRsvpRecipients } from "./hooks";
import { parseBroadcastCsv, extractItsIdsFromParsedCsv, normalizeItsIdFromCell } from "./utils";
import type { RaRecord } from "react-admin";
import type { FilterGroupData } from "./advanced-filter-builder/constants";
import {
  isMiqaatRsvpFilterEnvelope,
  type MiqaatRsvpFilterEnvelope,
} from "./miqaat-rsvp-filter-criteria";

type PreviewRecipientRow = {
  phone?: string | null;
  ITS_ID?: string | null;
  Full_Name?: string | null;
  householdKind?: string | null;
  householdKey?: string | null;
};

/** Template broadcasts: CSV ITS must exist in the ITS directory. */
const TemplateCsvUploadTab = () => {
  const { setValue } = useFormContext();
  const { updateSelectedPhones, updatePreviewStatus } = useRecipientSelection();
  const { data: lookupData, isLoading, error, lookup, reset } = useLookupIts();

  const [parsedIds, setParsedIds] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const parsedCsvRef = useRef<ReturnType<typeof parseBroadcastCsv> | null>(null);

  const handleFileUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setFileName(file.name);
      setCsvParseError(null);
      parsedCsvRef.current = null;
      reset();
      updatePreviewStatus(false);
      setValue("recipientCsvData", null, { shouldDirty: false });
      setValue("csvColumnHeaders", [], { shouldDirty: false });

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text !== "string") return;

        const parsed = parseBroadcastCsv(text);
        parsedCsvRef.current = parsed;

        if (!parsed.headers.length || !parsed.rows.length) {
          setCsvParseError("No data rows found in the file.");
          setParsedIds([]);
          return;
        }

        if (!parsed.itsHeader) {
          setCsvParseError(
            "Could not find an ITS column. Add a header named ITS, ITS_ID, or itsNo, or use a single column of ITS numbers."
          );
          setParsedIds([]);
          return;
        }

        const unique = extractItsIdsFromParsedCsv(parsed);
        if (unique.length === 0) {
          setCsvParseError("No valid ITS numbers found in the ITS column.");
          setParsedIds([]);
          return;
        }

        setParsedIds(unique);
        lookup(unique);
      };
      reader.readAsText(file);

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [lookup, reset, updatePreviewStatus, setValue]
  );

  useEffect(() => {
    if (!lookupData) return;

    const matched = lookupData.matched || [];
    if (matched.length > 0) {
      const recipients = matched
        .filter((r) => r.WhatsApp_No || r.Mobile)
        .map((r) => ({
          ...r,
          ITS_ID: (r.ITS_ID as string | null) || null,
          phone: String(r.WhatsApp_No || r.Mobile).trim(),
          Full_Name: (r.Full_Name as string | null) || null,
        }));

      updateSelectedPhones(recipients);
      updatePreviewStatus(true);

      setValue("recipientItsIds", matched.map((r) => r.ITS_ID).filter(Boolean) as string[], {
        shouldDirty: false,
      });

      const parsed = parsedCsvRef.current;
      if (parsed && parsed.itsHeader && parsed.rows.length) {
        const rowsByIts: Record<string, Record<string, string>> = {};
        parsed.rows.forEach((row) => {
          const id = normalizeItsIdFromCell(row[parsed.itsHeader!]);
          if (id) {
            const flat: Record<string, string> = {};
            Object.keys(row).forEach((k) => {
              flat[k] = row[k] === undefined || row[k] === null ? "" : String(row[k]);
            });
            rowsByIts[id] = flat;
          }
        });

        const csvDataByItsId: Record<string, Record<string, string>> = {};
        matched.forEach((r) => {
          const id = r.ITS_ID !== undefined && r.ITS_ID !== null ? String(r.ITS_ID).trim() : "";
          if (id && rowsByIts[id]) {
            csvDataByItsId[id] = rowsByIts[id];
          }
        });

        setValue(
          "recipientCsvData",
          Object.keys(csvDataByItsId).length > 0 ? csvDataByItsId : null,
          { shouldDirty: false }
        );
        setValue("csvColumnHeaders", parsed.headers.slice(), {
          shouldDirty: false,
        });
      } else {
        setValue("recipientCsvData", null, { shouldDirty: false });
        setValue("csvColumnHeaders", [], { shouldDirty: false });
      }
    } else {
      updateSelectedPhones([]);
      updatePreviewStatus(false);
      setValue("recipientItsIds", null, { shouldDirty: false });
      setValue("recipientCsvData", null, { shouldDirty: false });
      setValue("csvColumnHeaders", [], { shouldDirty: false });
    }
  }, [lookupData, updateSelectedPhones, updatePreviewStatus, setValue]);

  const matched = lookupData?.matched || [];
  const unmatched = lookupData?.unmatched || [];
  const paginatedMatched = matched.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2, py: 0.75 }}>
        Upload a CSV with an ITS column (&ldquo;ITS&rdquo;, &ldquo;ITS_ID&rdquo;, or
        &ldquo;itsNo&rdquo;) or a single column of ITS numbers. Extra columns can be mapped to
        template parameters under Column → CSV file. All ITS numbers are checked against the
        database.
      </Alert>

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
          Upload CSV
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            hidden
            onChange={handleFileUpload}
          />
        </Button>
        {fileName && (
          <Typography variant="body2" color="text.secondary">
            {fileName} — {parsedIds.length} ITS number
            {parsedIds.length !== 1 ? "s" : ""} parsed
          </Typography>
        )}
      </Stack>

      {isLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">Looking up {parsedIds.length} ITS numbers…</Typography>
        </Box>
      )}

      {csvParseError && (
        <Alert severity="error" sx={{ my: 2 }}>
          {csvParseError}
        </Alert>
      )}

      {error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {error?.message || "Failed to look up ITS data"}
        </Alert>
      )}

      {lookupData && !isLoading && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Chip label={`${matched.length} matched`} color="success" variant="outlined" />
            {unmatched.length > 0 && (
              <Chip label={`${unmatched.length} not found`} color="error" variant="outlined" />
            )}
          </Stack>

          {unmatched.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              The following ITS numbers were not found: <strong>{unmatched.join(", ")}</strong>
            </Alert>
          )}

          {matched.length > 0 && (
            <Card variant="outlined">
              <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: { xs: 500, sm: 600 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>ITS ID</TableCell>
                      <TableCell>Full Name</TableCell>
                      <TableCell>WhatsApp No</TableCell>
                      <TableCell>Sector</TableCell>
                      <TableCell>Sub Sector</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginatedMatched.map((record: RaRecord) => (
                      <TableRow key={String(record.ITS_ID ?? record.id)}>
                        <TableCell>{String(record.ITS_ID ?? "")}</TableCell>
                        <TableCell>{String(record.Full_Name ?? "")}</TableCell>
                        <TableCell>{String(record.WhatsApp_No || record.Mobile || "")}</TableCell>
                        <TableCell>{String(record.Sector ?? "")}</TableCell>
                        <TableCell>{String(record.Sub_Sector ?? "")}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={matched.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Card>
          )}

          {matched.filter((r) => r.WhatsApp_No || r.Mobile).length > 0 && (
            <Alert severity="success" sx={{ mt: 2, py: 0.5 }}>
              <Typography variant="body2" component="span" fontWeight="bold">
                {matched.filter((r) => r.WhatsApp_No || r.Mobile).length}
              </Typography>{" "}
              recipients with phone numbers will receive the broadcast
            </Alert>
          )}
        </>
      )}
    </Box>
  );
};

/**
 * Miqaat RSVP CSV is independent of Advanced Filter toggles. Each ITS is matched against
 * the ITS directory and Mutawatteen; preview/send use the expanded household phones.
 */
const MiqaatRsvpCsvUploadTab = () => {
  const { setValue, control } = useFormContext();
  const { updateSelectedPhones, updatePreviewStatus } = useRecipientSelection();
  const miqaatId = useWatch({ control, name: "miqaatId" }) as string | undefined;

  const [parsedIds, setParsedIds] = useState<string[]>([]);
  const [fileName, setFileName] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [csvParseError, setCsvParseError] = useState<string | null>(null);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileUpload = useCallback(
    (event: ChangeEvent<HTMLInputElement>) => {
      const file = event.target.files?.[0];
      if (!file) return;

      setFileName(file.name);
      setCsvParseError(null);
      updatePreviewStatus(false);
      updateSelectedPhones([]);
      setValue("recipientItsIds", null, { shouldDirty: false });
      setValue("recipientCsvData", null, { shouldDirty: false });
      setValue("csvColumnHeaders", [], { shouldDirty: false });
      setPage(0);

      const reader = new FileReader();
      reader.onload = (e) => {
        const text = e.target?.result;
        if (typeof text !== "string") return;

        const parsed = parseBroadcastCsv(text);

        if (!parsed.headers.length || !parsed.rows.length) {
          setCsvParseError("No data rows found in the file.");
          setParsedIds([]);
          return;
        }

        if (!parsed.itsHeader) {
          setCsvParseError(
            "Could not find an ITS column. Add a header named ITS, ITS_ID, or itsNo, or use a single column of ITS numbers."
          );
          setParsedIds([]);
          return;
        }

        const unique = extractItsIdsFromParsedCsv(parsed);
        if (unique.length === 0) {
          setCsvParseError("No valid ITS numbers found in the ITS column.");
          setParsedIds([]);
          return;
        }

        setParsedIds(unique);
        setValue("recipientItsIds", unique, { shouldDirty: false });
        setValue("csvColumnHeaders", parsed.headers.slice(), { shouldDirty: false });
      };
      reader.readAsText(file);

      if (fileInputRef.current) fileInputRef.current.value = "";
    },
    [setValue, updatePreviewStatus, updateSelectedPhones]
  );

  const previewQuery = usePreviewMiqaatRsvpRecipients({
    miqaatId,
    filterCriteria: null,
    recipientItsIds: parsedIds,
    enabled: parsedIds.length > 0 && Boolean(miqaatId?.trim()),
  });

  const recipients = (previewQuery.data?.data ?? []) as PreviewRecipientRow[];
  const outboundCount = previewQuery.data?.total ?? 0;
  const matchedItsIds = (previewQuery.data as { matchedItsIds?: string[] } | undefined)
    ?.matchedItsIds;
  const unmatchedItsIds = (previewQuery.data as { unmatchedItsIds?: string[] } | undefined)
    ?.unmatchedItsIds;
  const paginated = recipients.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage);

  useEffect(() => {
    if (parsedIds.length === 0) return;
    if (previewQuery.isLoading || previewQuery.isFetching) return;

    if (previewQuery.error) {
      updateSelectedPhones([]);
      updatePreviewStatus(false);
      return;
    }

    const rows = (previewQuery.data?.data ?? []) as PreviewRecipientRow[];
    if (!previewQuery.data) return;

    const selected = rows
      .filter((r) => r.phone && String(r.phone).trim())
      .map((r) => ({
        ITS_ID: r.ITS_ID ?? null,
        phone: String(r.phone).trim(),
        Full_Name: r.Full_Name ?? null,
      }));

    updateSelectedPhones(selected);
    updatePreviewStatus(selected.length > 0);
    setValue("_recipientCount", previewQuery.data.total ?? 0, { shouldDirty: false });
  }, [
    parsedIds.length,
    previewQuery.isLoading,
    previewQuery.isFetching,
    previewQuery.error,
    previewQuery.data,
    updateSelectedPhones,
    updatePreviewStatus,
    setValue,
  ]);

  return (
    <Box>
      <Alert severity="info" sx={{ mb: 2, py: 0.75 }}>
        CSV upload is independent of the Advanced Filter ITS / Mutawatteen toggles. Each ITS number
        is matched against <strong>both</strong> the ITS directory and Mutawatteen; matching
        households expand to WhatsApp numbers for the send.
      </Alert>

      {!miqaatId?.trim() && (
        <Alert severity="warning" sx={{ mb: 2, py: 0.75 }}>
          Select a miqaat first so recipients can be previewed.
        </Alert>
      )}

      <Stack
        direction={{ xs: "column", sm: "row" }}
        spacing={{ xs: 1, sm: 2 }}
        alignItems={{ xs: "flex-start", sm: "center" }}
        sx={{ mb: 2, flexWrap: "wrap" }}
      >
        <Button variant="outlined" component="label" startIcon={<UploadFileIcon />}>
          Upload CSV
          <input
            ref={fileInputRef}
            type="file"
            accept=".csv,.txt"
            hidden
            onChange={handleFileUpload}
          />
        </Button>
        {fileName && (
          <Typography variant="body2" color="text.secondary">
            {fileName} — {parsedIds.length} ITS number
            {parsedIds.length !== 1 ? "s" : ""} parsed
          </Typography>
        )}
      </Stack>

      {csvParseError && (
        <Alert severity="error" sx={{ my: 2 }}>
          {csvParseError}
        </Alert>
      )}

      {parsedIds.length > 0 && previewQuery.isLoading && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, my: 2 }}>
          <CircularProgress size={20} />
          <Typography variant="body2">
            Matching {parsedIds.length} ITS numbers against ITS directory and Mutawatteen…
          </Typography>
        </Box>
      )}

      {previewQuery.error && (
        <Alert severity="error" sx={{ my: 2 }}>
          {(previewQuery.error as Error)?.message || "Failed to preview Miqaat RSVP recipients"}
        </Alert>
      )}

      {parsedIds.length > 0 && previewQuery.data && !previewQuery.isLoading && (
        <>
          <Stack direction="row" spacing={2} sx={{ mb: 2, flexWrap: "wrap" }}>
            <Chip
              label={`${matchedItsIds?.length ?? 0} ITS matched`}
              color="success"
              variant="outlined"
            />
            {(unmatchedItsIds?.length ?? 0) > 0 && (
              <Chip
                label={`${unmatchedItsIds!.length} ITS not found`}
                color="error"
                variant="outlined"
              />
            )}
            <Chip
              label={`${outboundCount} outbound phone${outboundCount !== 1 ? "s" : ""}`}
              color="primary"
              variant="outlined"
            />
          </Stack>

          {(unmatchedItsIds?.length ?? 0) > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              Not found in ITS directory or Mutawatteen:{" "}
              <strong>{unmatchedItsIds!.join(", ")}</strong>
            </Alert>
          )}

          {recipients.length > 0 && (
            <Card variant="outlined">
              <TableContainer sx={{ maxHeight: 500, overflowX: "auto" }}>
                <Table size="small" stickyHeader sx={{ minWidth: { xs: 450, sm: 550 } }}>
                  <TableHead>
                    <TableRow>
                      <TableCell>Phone</TableCell>
                      <TableCell>ITS ID</TableCell>
                      <TableCell>Name</TableCell>
                      <TableCell>Household</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {paginated.map((row, index) => (
                      <TableRow key={`${row.phone ?? "p"}-${row.householdKey ?? index}`}>
                        <TableCell>{row.phone ?? ""}</TableCell>
                        <TableCell>{row.ITS_ID ?? "—"}</TableCell>
                        <TableCell>{row.Full_Name ?? "—"}</TableCell>
                        <TableCell>
                          {row.householdKind === "SABIL" ? "Mutawatteen" : "ITS"}
                          {row.householdKey ? ` · ${row.householdKey}` : ""}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
              <TablePagination
                component="div"
                count={recipients.length}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                  setRowsPerPage(parseInt(e.target.value, 10));
                  setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
              />
            </Card>
          )}
        </>
      )}
    </Box>
  );
};

const CsvUploadTab = () => {
  const broadcastKind = useWatch({ name: "broadcastKind" }) as string | undefined;
  if (broadcastKind === "MIQAAT_RSVP") {
    return <MiqaatRsvpCsvUploadTab />;
  }
  return <TemplateCsvUploadTab />;
};

const RecipientFilters = () => {
  const { setValue, control } = useFormContext();
  const { updateSelectedPhones, updatePreviewStatus } = useRecipientSelection();

  const broadcastKind = useWatch({ control, name: "broadcastKind" }) as string | undefined;
  const sabilType = useWatch({ control, name: "sabilType" }) as string | undefined;
  const filterCriteria = useWatch({ control, name: "filterCriteria" }) as
    | FilterGroupData
    | MiqaatRsvpFilterEnvelope
    | undefined;
  const [tab, setTab] = useState(0);

  const handleTabChange = useCallback(
    (_: SyntheticEvent, newTab: number) => {
      setTab(newTab);
      updateSelectedPhones([]);
      updatePreviewStatus(false);
      setValue("recipientItsIds", null, { shouldDirty: false });
      setValue("recipientCsvData", null, { shouldDirty: false });
      setValue("csvColumnHeaders", [], { shouldDirty: false });
    },
    [updateSelectedPhones, updatePreviewStatus, setValue]
  );

  if (broadcastKind === "EVENT_RSVP") {
    return (
      <EventRsvpRecipientPanel
        onPreviewCount={(count) => {
          setValue("_recipientCount", count, { shouldDirty: false });
        }}
        onPreviewed={updatePreviewStatus}
        onSelectionChange={updateSelectedPhones}
      />
    );
  }

  if (broadcastKind === "FMB_OUTSTANDING") {
    return (
      <FmbOutstandingRecipientPanel
        onPreviewCount={(count) => {
          setValue("_recipientCount", count, { shouldDirty: false });
        }}
        onPreviewed={updatePreviewStatus}
        onSelectionChange={updateSelectedPhones}
      />
    );
  }

  if (broadcastKind === "SABIL_OUTSTANDING") {
    return (
      <SabilOutstandingRecipientPanel
        // Remount on sub-tab switch (Chula/Establishment) so stale preview state isn't carried over.
        key={sabilType}
        onPreviewCount={(count) => {
          setValue("_recipientCount", count, { shouldDirty: false });
        }}
        onPreviewed={updatePreviewStatus}
        onSelectionChange={updateSelectedPhones}
      />
    );
  }

  if (broadcastKind === "MUTTAVATTEEN_OUTSTANDING") {
    return (
      <MuttavatteenOutstandingRecipientPanel
        // Remount on sub-tab switch (Monthly/Yearly) so stale preview state isn't carried over.
        key={sabilType}
        onPreviewCount={(count) => {
          setValue("_recipientCount", count, { shouldDirty: false });
        }}
        onPreviewed={updatePreviewStatus}
        onSelectionChange={updateSelectedPhones}
      />
    );
  }

  return (
    <Box sx={{ width: "100%", minWidth: 0 }}>
      <Box sx={{ width: "100%", maxWidth: "100%", minWidth: 0 }}>
        <Tabs
          value={tab}
          onChange={handleTabChange}
          sx={{
            mb: 2,
            borderBottom: 1,
            borderColor: "divider",
            width: "100%",
            maxWidth: "100%",
            "& .MuiTabScrollButton-root:not(.Mui-disabled):not([disabled])": {
              width: 32,
              height: 32,
              my: "auto",
              mx: 0.5,
              borderRadius: "50%",
              backgroundColor: "background.paper",
              boxShadow: 1,
              border: 1,
              borderColor: "divider",
              color: "text.primary",
              opacity: 0.9,
              flexShrink: 0,
              zIndex: 2,
              transition: "opacity 0.2s, transform 0.2s, background-color 0.2s",
              "&:hover": {
                backgroundColor: "action.hover",
                opacity: 1,
                transform: "scale(1.05)",
              },
            },
            "& .MuiTabs-scrollButtons.Mui-disabled, & .MuiTabScrollButton-root.Mui-disabled, & .MuiTabScrollButton-root[disabled]":
              {
                display: "none !important",
                width: "0 !important",
                minWidth: "0 !important",
                opacity: "0 !important",
                visibility: "hidden !important",
                pointerEvents: "none !important",
                margin: "0 !important",
                padding: "0 !important",
                border: "none !important",
                boxShadow: "none !important",
              },
          }}
          variant="scrollable"
          scrollButtons="auto"
          allowScrollButtonsMobile
        >
          <Tab label="Advanced Filter" />
          <Tab label="Upload CSV (ITS Numbers)" />
        </Tabs>
      </Box>

      {tab === 0 &&
        (broadcastKind === "MIQAAT_RSVP" ? (
          <MiqaatRsvpAudienceFilters
            value={filterCriteria}
            onChange={(envelope) => {
              setValue("filterCriteria", envelope);
              updatePreviewStatus(false);
            }}
            onSelectionChange={updateSelectedPhones}
            onPreviewed={updatePreviewStatus}
          />
        ) : (
          <AdvancedFilterBuilder
            value={isMiqaatRsvpFilterEnvelope(filterCriteria) ? undefined : filterCriteria}
            onChange={(filterGroup) => {
              setValue("filterCriteria", filterGroup);
              updatePreviewStatus(false);
            }}
            onSelectionChange={updateSelectedPhones}
            onPreviewed={updatePreviewStatus}
          />
        ))}

      {tab === 1 && <CsvUploadTab />}
    </Box>
  );
};

export default RecipientFilters;
